import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  MdArrowBack,
  MdTrendingUp,
  MdTrendingDown,
  MdAccountBalance,
  MdWarning,
  MdCheckCircle,
  MdError,
  MdInfo
} from 'react-icons/md';
import financeHealthService, { FinanceHealthData } from '../../services/financeHealthService';

const FinanceHealth: React.FC = () => {
  const navigate = useNavigate();
  const [data, setData] = useState<FinanceHealthData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    loadFinanceHealth();
  }, []);

  const loadFinanceHealth = async (): Promise<void> => {
    try {
      setLoading(true);
      const healthData = await financeHealthService.getFinanceHealth();
      setData(healthData);
    } catch (error) {
      console.error('Error loading finance health:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical': return <MdError />;
      case 'warning': return <MdWarning />;
      case 'success': return <MdCheckCircle />;
      default: return <MdInfo />;
    }
  };

  if (loading) {
    return (
      <Container>
        <LoadingState>
          <LoadingSpinner />
          <p>Calculating financial health...</p>
        </LoadingState>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container>
        <ErrorState>
          <MdError style={{ fontSize: '64px', color: '#DC2626' }} />
          <h2>Unable to Load Data</h2>
          <p>Please try again later.</p>
        </ErrorState>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate('/dashboard')}>
          <MdArrowBack />
          <span>Back to Dashboard</span>
        </BackButton>
        <Title>Finance Health</Title>
        <Subtitle>Real-time financial health monitoring and insights</Subtitle>
      </Header>

      {/* Health Score */}
      <HealthScoreSection>
        <HealthScoreCard>
          <ScoreLabel>Overall Health Score</ScoreLabel>
          <ScoreCircle $color={data.statusColor}>
            <ScoreValue>{data.healthScore}</ScoreValue>
            <ScoreMax>/100</ScoreMax>
          </ScoreCircle>
          <ScoreStatus $color={data.statusColor}>
            {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
          </ScoreStatus>
          <ScoreDescription>
            Based on cash position, liquidity, burn rate, and revenue growth
          </ScoreDescription>
        </HealthScoreCard>
      </HealthScoreSection>

      {/* Key Metrics */}
      <MetricsGrid>
        {/* Cash Position */}
        <MetricCard>
          <MetricHeader>
            <MetricIcon $color="#1e3a8a">
              <MdAccountBalance />
            </MetricIcon>
            <MetricLabel>{data.metrics.cashPosition.label}</MetricLabel>
          </MetricHeader>
          <MetricValue>
            {formatCurrency(data.metrics.cashPosition.amount || 0)}
          </MetricValue>
          <MetricStatus $status={data.metrics.cashPosition.status}>
            {data.metrics.cashPosition.status === 'positive' ? 'Healthy' : 'Low'}
          </MetricStatus>
        </MetricCard>

        {/* Quick Ratio */}
        <MetricCard>
          <MetricHeader>
            <MetricIcon $color="#059669">
              <MdTrendingUp />
            </MetricIcon>
            <MetricLabel>{data.metrics.quickRatio.label}</MetricLabel>
          </MetricHeader>
          <MetricValue>{data.metrics.quickRatio.value}</MetricValue>
          <MetricStatus $status={data.metrics.quickRatio.status}>
            Benchmark: {data.metrics.quickRatio.benchmark}
          </MetricStatus>
        </MetricCard>

        {/* Days of Cash */}
        <MetricCard>
          <MetricHeader>
            <MetricIcon $color="#7c3aed">
              <MdTrendingUp />
            </MetricIcon>
            <MetricLabel>{data.metrics.daysOfCash.label}</MetricLabel>
          </MetricHeader>
          <MetricValue>
            {typeof data.metrics.daysOfCash.days === 'number' 
              ? `${data.metrics.daysOfCash.days} days`
              : data.metrics.daysOfCash.days}
          </MetricValue>
          <MetricStatus $status={data.metrics.daysOfCash.status}>
            {data.metrics.daysOfCash.status === 'good' ? 'Strong Runway' : 
             data.metrics.daysOfCash.status === 'warning' ? 'Monitor Closely' : 'Critical'}
          </MetricStatus>
        </MetricCard>

        {/* Burn Rate */}
        <MetricCard>
          <MetricHeader>
            <MetricIcon $color="#DC2626">
              <MdTrendingDown />
            </MetricIcon>
            <MetricLabel>{data.metrics.burnRate.label}</MetricLabel>
          </MetricHeader>
          <MetricValue>
            {formatCurrency(data.metrics.burnRate.amount || 0)}
          </MetricValue>
          <MetricStatus $status={data.metrics.burnRate.trend === 'decreasing' ? 'good' : 'warning'}>
            {data.metrics.burnRate.trend === 'increasing' ? 'Increasing' : 
             data.metrics.burnRate.trend === 'decreasing' ? 'Decreasing' : 'Stable'}
          </MetricStatus>
        </MetricCard>
      </MetricsGrid>

      {/* Revenue Growth */}
      <RevenueSection>
        <SectionTitle>Revenue Growth</SectionTitle>
        <RevenueCard>
          <RevenueValue $positive={Number(data.metrics.revenueGrowth.percentage) >= 0}>
            {Number(data.metrics.revenueGrowth.percentage) >= 0 ? '+' : ''}
            {data.metrics.revenueGrowth.percentage}%
          </RevenueValue>
          <RevenueLabel>Month-over-Month Growth</RevenueLabel>
          {data.trends.revenue.length > 0 && (
            <TrendChart>
              {data.trends.revenue.map((item, idx) => (
                <TrendBar key={idx}>
                  <BarLabel>{item.period.substring(5)}</BarLabel>
                  <BarContainer>
                    <Bar 
                      $height={(item.amount / Math.max(...data.trends.revenue.map(t => t.amount))) * 100}
                      $color="#1e3a8a"
                    />
                  </BarContainer>
                  <BarValue>{formatCurrency(item.amount)}</BarValue>
                </TrendBar>
              ))}
            </TrendChart>
          )}
        </RevenueCard>
      </RevenueSection>

      {/* Alerts */}
      <AlertsSection>
        <SectionTitle>Alerts & Recommendations</SectionTitle>
        {data.alerts.map((alert, idx) => (
          <Alert key={idx} $type={alert.type}>
            <AlertIcon>{getAlertIcon(alert.type)}</AlertIcon>
            <AlertContent>
              <AlertTitle>{alert.title}</AlertTitle>
              <AlertMessage>{alert.message}</AlertMessage>
            </AlertContent>
          </Alert>
        ))}
      </AlertsSection>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  padding: 32px;
  max-width: 1600px;
  margin: 0 auto;
  background: #F8F9FA;
  min-height: calc(100vh - 64px);

  @media (max-width: 768px) {
    padding: 20px;
  }
`;

const Header = styled.div`
  margin-bottom: 32px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 16px;
  background: white;
  border: 1px solid #E5E7EB;
  border-radius: 8px;
  color: #6B7280;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  margin-bottom: 16px;

  svg {
    font-size: 20px;
  }

  &:hover {
    border-color: #1e3a8a;
    color: #1e3a8a;
    background: #eff6ff;
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 8px 0;
`;

const Subtitle = styled.p`
  color: #6B7280;
  font-size: 16px;
  margin: 0;
`;

const LoadingState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 100px 20px;
  gap: 16px;

  p {
    color: #6B7280;
    font-size: 15px;
  }
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #E5E7EB;
  border-top-color: #1e3a8a;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const ErrorState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 100px 20px;
  text-align: center;

  h2 {
    font-size: 24px;
    color: #111827;
    margin: 16px 0 8px 0;
  }

  p {
    color: #6B7280;
  }
`;

const HealthScoreSection = styled.div`
  margin-bottom: 32px;
`;

const HealthScoreCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 48px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const ScoreLabel = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #6B7280;
  margin-bottom: 24px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ScoreCircle = styled.div<{ $color: string }>`
  width: 200px;
  height: 200px;
  border-radius: 50%;
  border: 12px solid ${props => props.$color};
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  position: relative;
  background: ${props => props.$color}10;
`;

const ScoreValue = styled.div`
  font-size: 72px;
  font-weight: 700;
  color: #111827;
  line-height: 1;
`;

const ScoreMax = styled.div`
  font-size: 24px;
  color: #6B7280;
  font-weight: 500;
`;

const ScoreStatus = styled.div<{ $color: string }>`
  font-size: 24px;
  font-weight: 700;
  color: ${props => props.$color};
  margin-bottom: 12px;
  text-transform: uppercase;
  letter-spacing: 1px;
`;

const ScoreDescription = styled.p`
  color: #6B7280;
  font-size: 14px;
  max-width: 500px;
  margin: 0;
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 24px;
  margin-bottom: 32px;
`;

const MetricCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
  transition: all 0.2s;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  }
`;

const MetricHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const MetricIcon = styled.div<{ $color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$color};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
  }
`;

const MetricLabel = styled.div`
  font-size: 14px;
  color: #6B7280;
  font-weight: 500;
`;

const MetricValue = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
`;

const MetricStatus = styled.div<{ $status: string }>`
  font-size: 13px;
  font-weight: 500;
  color: ${props => 
    props.$status === 'good' || props.$status === 'positive' ? '#059669' :
    props.$status === 'warning' ? '#F59E0B' : '#6B7280'
  };
`;

const RevenueSection = styled.div`
  margin-bottom: 32px;
`;

const SectionTitle = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
`;

const RevenueCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 32px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const RevenueValue = styled.div<{ $positive: boolean }>`
  font-size: 48px;
  font-weight: 700;
  color: ${props => props.$positive ? '#059669' : '#DC2626'};
  margin-bottom: 8px;
`;

const RevenueLabel = styled.div`
  font-size: 16px;
  color: #6B7280;
  margin-bottom: 32px;
`;

const TrendChart = styled.div`
  display: flex;
  gap: 16px;
  align-items: flex-end;
  justify-content: space-between;
`;

const TrendBar = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const BarLabel = styled.div`
  font-size: 12px;
  color: #6B7280;
  font-weight: 500;
`;

const BarContainer = styled.div`
  width: 100%;
  height: 150px;
  background: #F3F4F6;
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: flex-end;
  overflow: hidden;
`;

const Bar = styled.div<{ $height: number; $color: string }>`
  width: 100%;
  height: ${props => props.$height}%;
  background: ${props => props.$color};
  transition: height 0.3s ease;
  border-radius: 8px 8px 0 0;
`;

const BarValue = styled.div`
  font-size: 11px;
  color: #111827;
  font-weight: 600;
`;

const AlertsSection = styled.div`
  margin-bottom: 32px;
`;

const Alert = styled.div<{ $type: string }>`
  background: ${props => 
    props.$type === 'critical' ? '#FEE2E2' :
    props.$type === 'warning' ? '#FEF3C7' :
    props.$type === 'success' ? '#D1FAE5' : '#DBEAFE'
  };
  border-left: 4px solid ${props =>
    props.$type === 'critical' ? '#DC2626' :
    props.$type === 'warning' ? '#F59E0B' :
    props.$type === 'success' ? '#059669' : '#3B82F6'
  };
  border-radius: 8px;
  padding: 16px;
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
`;

const AlertIcon = styled.div`
  font-size: 24px;
  color: inherit;
  display: flex;
  align-items: flex-start;
`;

const AlertContent = styled.div`
  flex: 1;
`;

const AlertTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
`;

const AlertMessage = styled.div`
  font-size: 14px;
  color: #374151;
`;

export default FinanceHealth;
